<template>
<div>
  <GlobalEvents />

  <div class="mdc-text-field input__field mdc-text-field--outlined mdc-text-field--with-trailing-icon"
       :class="{'mdc-text-field--valid': validState,
       'mdc-text-field--invalid': invalid}"
  >
    <bk-outline-notched :id="id" :label="label" :pre-filled="!searchTerm.isEmpty()" />
    <input :value="searchTerm"
           @input="onInput"
           @focus="$emit('focus')"
           @blur="onBlur"
           @keydown.down="keyupDown"
           @keydown.up="keyupUp"
           @keydown.enter="keyupEnter"
           autocomplete="off"
           type="text"
           :id="id"
           class="mdc-text-field__input"
           :aria-controls="helperTextId"
           :aria-describedby="helperTextId"
           :class="{
             'data-hj-allow': allowHotjarRecording,
             'data-hj-suppress': !allowHotjarRecording
           }"
    >

    <i v-if="loading" class="mdc-text-field__icon mdc-text-field__icon--trailing" tabindex="-1">
      <bk-loading-spinner></bk-loading-spinner>
    </i>
    <i v-else class="material-icons mdc-text-field__icon mdc-text-field__icon--trailing" tabindex="0" role="button">{{ icon }}</i>

  </div>
  <div class="mdc-menu-surface--anchor">
    <div class="mdc-menu mdc-menu-surface">
      <ul class="mdc-list" role="menu" aria-orientation="vertical" :class="{'mdc-list--max-height': list.length > 5}">
        <li class="mdc-list-item"
            v-for="(listItem, index) in list"
            :class="{ 'mdc-list-item--selected': selectedIndex === index }"
            role="menuitem"
            @click="select(listItem)"
        >
          <span class="mdc-list-item__text">{{listItem.city}} ({{listItem.district}})</span>
        </li>
      </ul>
    </div>
  </div>
  <div class="mdc-text-field-helper-line">
    <p :id="helperTextId"
      class="mdc-text-field-helper-text mdc-text-field-helper-text--persistent"
      :class="{'mdc-text-field-helper-text--disabled': !showHelper}"
      aria-hidden="true"
    >{{helperText}}</p>
  </div>

</div>
</template>


<script>
import { MDCMenu } from '@material/menu'
import { MDCTextField } from '@material/textfield'
import BkOutlineNotched from './BkOutlineNotched'
import BkLoadingSpinner from './BkLoadingSpinner'
import GlobalEvents from 'vue-global-events/src'
import debounce from 'lodash.debounce'

export default {
  name: 'bk-search-field',
  data () {
    return {
      mdcTextField: null,
      mdcMenu: null,
      searchTerm: '',
      selectedIndex: 0,
      pristine: true,
    }
  },
  components: { GlobalEvents, BkOutlineNotched, BkLoadingSpinner },
  props: {
    /**
     * Allow recording in HotJar by setting the appropriate classes to the input field
     */
    allowHotjarRecording: {
      type: Boolean,
      default: false
    },
    /**
     * Label of the search field
     */
    label: String,
    /**
     * HelperText for the search field
     */
    helperText: String,
    /**
     * List of found suggestions
     */
    list: Array,
    /**
     * Trailing icon
     */
    icon: String,
    /**
     * The current value of the search field
     */
    value: String,
    /**
     * Is true if the field is valid
     */
    valid: {
      type: Boolean,
      default: true
    },
    /**
     * Identifier of the field
     */
    id: {
      type: String,
      required: true,
    },

    loading: {
      type: Boolean,
      default: false,
    }
  },
  mounted () {
    this.mdcTextField = new MDCTextField(this.$el.querySelector('.mdc-text-field'))
    this.mdcTextField.useNativeValidation = false
    this.mdcMenu = new MDCMenu(this.$el.querySelector('.mdc-menu'))
  },
  computed: {
    /**
     * return true if the helper text should show
     * @returns {boolean}
     */
    showHelper () {
      return this.mdcMenu ? !this.mdcMenu.open : true
    },
    /**
     * return the id of the helper text
     * @returns {string}
     */
    helperTextId () {
      return `${this.id}-helper-text`
    },
    /**
     * return true if the element is valid and dont pristine
     */
    validState () {
      return this.valid && !this.pristine
    },
    invalid () {
      let isInvalid = !(this.valid || this.pristine)
      if (this.mdcTextField) this.mdcTextField.valid = !isInvalid
      return isInvalid
    },
  },
  methods: {
    /**
     * Fires the click event and update the state
     * @param item
     */
    select (item) {
      this.$emit('click', item)
    },
    /**
     * Called if the input event was catched
     */
    onInput (event) {
      this.searchTerm = event.target.value
      this.debounceOnInput()
    },

    debounceOnInput: debounce(function () {
      this.pristine = false
      this.mdcMenu.open = this.searchTerm.length > 0
      this.$emit('input', this.searchTerm)
    }, 300),

    /**
     * Set the select index to the next valid index
     * if the the index is out of range set the selected index to the first index
     */
    keyupDown () {
      this.selectedIndex = (this.selectedIndex + 1) % this.list.length
    },
    /**
     * Set the selected index to the previous index
     * if the index is out of range set the index to the last index
     */
    keyupUp () {
      this.selectedIndex--
      if (this.selectedIndex < 0) this.selectedIndex = this.list.length - 1
    },
    /**
     * Close the menut and select the item on the current selected index
     * @param event
     */
    keyupEnter (event) {
      this.select(this.list[this.selectedIndex])
      event.target.blur()
      this.mdcMenu.open = false
    },
    onBlur (event) {
      this.select(this.list[this.selectedIndex])
      this.mdcMenu.open = false
      this.$emit('blur')
    }
  },
  watch: {
    value () {
      this.searchTerm = this.value
    },
    list() {
      this.selectedIndex = 0
    },
  },
}

</script>

<style lang="scss" scoped>
  .input__field {
    width: 100%;
    max-width: 320px;
  }

  .mdc-text-field-helper-text {
    margin-left: 0;
    margin-right: 0;

    &--disabled {
      display: none;
    }
  }

  .mdc-menu {
    overflow-y: auto;

    &-surface {
      width: 100%;

      &--anchor {
        margin-top: 8px;
      }
    }
  }

  .mdc-menu .mdc-list {
    padding-left: 0 !important;
  }

  .mdc-list--max-height {
    height: calc(5 * 48px);
  }

  .mdc-menu-surface--anchor {
    margin-top: 0;
  }
</style>
